import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Star } from 'lucide-react'
import QRCode from 'qrcode'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { useLanguage } from '../contexts/LanguageContext'
import { useCart } from '../contexts/CartContext'
import { useRestaurant } from '../contexts/RestaurantContext'
import { addQROrder } from '../utils/recentActivity'

const ORDER_SUCCESS_LOTTIE_URL = 'https://lottie.host/16bd8326-3901-4ded-a8fd-07f2366892ba/AyxGeGKDpI.lottie'
const GOOGLE_G_LOGO_URL = 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/500px-Google_%22G%22_logo.svg.png'

/** Payload encoded in the QR for the waiter module to scan and load into cart. */
export interface OrderQRPayload {
  v: number
  cn?: string
  cm?: string
  n?: string
  i: Array<{
    mi: string
    na: string
    q: number
    vn?: string
    vpm?: number
    ad?: string[]
    ap?: number
    bp: number
    no?: string
  }>
}

export default function OrderQRSuccess() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()
  const { clearCart } = useCart()
  const { restaurantInfo, taxRate = 0, serviceChargeRate = 0 } = useRestaurant()
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const state = location.state as {
    items: Array<{
      menu_item_id: string
      name: string
      base_price: number
      variant_name?: string
      variant_price_modifier?: number
      addons?: string[]
      addons_price?: number
      quantity: number
      notes?: string
    }>
    orderNotes?: string
    customerName?: string
    customerMobile?: string
  } | null

  useEffect(() => {
    if (!state?.items?.length) {
      navigate('/', { replace: true })
      return
    }

    const payload: OrderQRPayload = {
      v: 1,
      cn: state.customerName?.trim() || undefined,
      cm: state.customerMobile?.trim() || undefined,
      n: state.orderNotes?.trim() || undefined,
      i: state.items.map(item => ({
        mi: item.menu_item_id,
        na: item.name,
        q: item.quantity,
        vn: item.variant_name,
        vpm: item.variant_price_modifier,
        ad: item.addons?.length ? item.addons : undefined,
        ap: item.addons_price,
        bp: item.base_price,
        no: item.notes
      }))
    }

    const json = JSON.stringify(payload)
    const subtotal = state.items.reduce((sum, item) => {
      const unit = item.base_price + (item.variant_price_modifier ?? 0) + (item.addons_price ?? 0)
      return sum + unit * item.quantity
    }, 0)
    const tax = (subtotal * (taxRate || 0)) / 100
    const service = (subtotal * (serviceChargeRate || 0)) / 100
    const total = subtotal + tax + service
    addQROrder({
      id: `qr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      created_at: new Date().toISOString(),
      customer_name: state.customerName?.trim() || undefined,
      customer_mobile: state.customerMobile?.trim() || undefined,
      order_notes: state.orderNotes?.trim() || undefined,
      items: state.items.map((item) => {
        const unitPrice = item.base_price + (item.variant_price_modifier ?? 0) + (item.addons_price ?? 0)
        return {
          name: item.name,
          quantity: item.quantity,
          unit_price: unitPrice,
          total_price: unitPrice * item.quantity,
          variant_name: item.variant_name,
          addons: item.addons?.length ? item.addons : undefined,
          notes: item.notes
        }
      }),
      subtotal,
      total,
      status: 'pending_scan',
      qrPayload: payload as import('../utils/recentActivity').StoredQRPayload
    })
    QRCode.toDataURL(json, { width: 280, margin: 2 })
      .then((dataUrl) => {
        setQrDataUrl(dataUrl)
        clearCart()
      })
      .catch(() => setError('Failed to generate QR code'))
  }, [state, navigate, clearCart, taxRate, serviceChargeRate])

  if (!state?.items?.length) return null

  return (
    <div className="min-h-screen bg-[#fafafa] pb-8">
      <header className="sticky top-0 z-30 bg-[#fafafa] border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="p-2 -m-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t('backToMenu')}
          >
            <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
          </button>
          <h1 className="text-base font-bold text-gray-800">{t('yourOrder')}</h1>
          <div className="w-9" aria-hidden />
        </div>
      </header>
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
            <DotLottieReact
              src={ORDER_SUCCESS_LOTTIE_URL}
              loop={false}
              autoplay
              style={{ width: '96px', height: '96px' }}
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('orderSuccess')}
          </h1>
          <p className="text-gray-600">
            {t('showQRToWaiter')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col items-center">
          {error && (
            <p className="text-red-600 text-sm mb-4">{error}</p>
          )}
          {qrDataUrl && (
            <div className="qr-border-wrap w-64 h-64 mx-auto">
              <div className="qr-border-inner w-full h-full">
                <img
                  src={qrDataUrl}
                  alt="Order QR Code"
                  className="w-full h-full rounded-md object-contain"
                />
              </div>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-4 text-center">
            {t('waiterScanHint')}
          </p>

          {/* Order details */}
          <div className="mt-6 w-full border-t border-gray-100 pt-4 text-left">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('orderDetails')}</h3>
            <ul className="space-y-3">
              {state.items.map((item, index) => {
                const unitPrice = item.base_price + (item.variant_price_modifier ?? 0) + (item.addons_price ?? 0)
                const lineTotal = unitPrice * item.quantity
                return (
                  <li key={`${item.menu_item_id}-${index}`} className="text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="font-medium text-gray-900">
                        {item.name} × {item.quantity}
                      </span>
                      <span className="text-gray-700 shrink-0">{lineTotal.toFixed(2)}</span>
                    </div>
                    {item.variant_name && (
                      <p className="text-gray-500 mt-0.5">{item.variant_name}</p>
                    )}
                    {item.addons && item.addons.length > 0 && (
                      <p className="text-gray-500 mt-0.5">{item.addons.join(', ')}</p>
                    )}
                    {item.notes && (
                      <p className="text-gray-500 mt-0.5 italic">{item.notes}</p>
                    )}
                  </li>
                )
              })}
            </ul>
            {state.orderNotes && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500">{t('specialRequests')}</p>
                <p className="text-sm text-gray-700 mt-0.5">{state.orderNotes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Leave a review on Google Maps */}
        <a
          href={restaurantInfo?.google_maps_review_url || 'https://search.google.com/local/writereview'}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 block w-full review-google-btn-border shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.99] focus:outline-none focus:ring-4 focus:ring-amber-300/50"
        >
          <div className="review-google-btn-bg relative px-6 py-4 flex items-center justify-start gap-4 rounded-[calc(1rem-2px)] overflow-hidden">
            <div className="absolute inset-0 bg-white/5" aria-hidden />
            <img
              src={GOOGLE_G_LOGO_URL}
              alt=""
              className="w-12 h-12 shrink-0 object-contain"
            />
            <span className="relative font-medium text-sm tracking-tight text-gray-800 flex flex-col items-start text-left leading-tight gap-0.5">
              <span className="flex items-center gap-1 flex-nowrap whitespace-nowrap">
                {t('leaveReviewOnLine1')}
                <span className="flex items-center gap-0.5 shrink-0" aria-hidden>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" strokeWidth={1.5} />
                  ))}
                </span>
              </span>
              <span className="font-semibold text-base text-gray-900">{t('leaveReviewOnLine2')}</span>
            </span>
          </div>
        </a>
      </div>
    </div>
  )
}
